import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

export const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg.replace(/^Value error, /, "") : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authModal, setAuthModal] = useState({ open: false, mode: "login" });

  useEffect(() => {
    // CRITICAL: If returning from the Google OAuth callback, skip the /me check —
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => setUser(r.data))
      .catch(() => setUser(null))
      .then(() => setLoading(false));
  }, []);

  const signOut = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {
      // ignore network errors, clear local state regardless
    }
    setUser(null);
  }, []);

  const signInWithPassword = useCallback(async (email, password) => {
    const r = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    setUser(r.data.user);
    return r.data.user;
  }, []);

  const registerWithPassword = useCallback(async (name, email, password) => {
    const r = await axios.post(
      `${API}/auth/register`,
      { name, email, password },
      { withCredentials: true }
    );
    setUser(r.data.user);
    return r.data.user;
  }, []);

  const openAuth = useCallback((mode = "login") => setAuthModal({ open: true, mode }), []);
  const closeAuth = useCallback(() => setAuthModal((m) => ({ ...m, open: false })), []);

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        loading,
        signOut,
        signInWithPassword,
        registerWithPassword,
        authModal,
        openAuth,
        closeAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
